import React, { useContext, useState, useEffect, forwardRef } from 'react'
import { selectData } from 'layouts/onboarding'
import ValuePoint from 'admin/components/ValuePoint'
import { BalanceSheetRow } from 'layouts/authenticated/pages/dashboard/components/BalanceSheet/BalanceSheetRow'
import {
  Card,
  Grid,
  Typography,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogContent,
  IconButton,
  Grow,
  Box,
  Icon,
} from '@mui/material'
import { motion } from 'framer-motion'
import { ArrowBack, Close, ExpandMore } from '@mui/icons-material'
import { BalanceSheetModalForm } from 'layouts/authenticated/pages/dashboard'
import { NewBalanceSheetModalForm } from 'layouts/authenticated/pages/newdash/components/NewBalanceSheetModalForm'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import NetWorthTable from 'layouts/authenticated/pages/newdash/components/NetWorthTable'
const Transition = forwardRef(function Transition(props, ref) {
  return <Grow timeout={300} ref={ref} {...props} />
})

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})
function BalanceSheet({
  collapseSections,
  toggleCollapse,
  liabilities,
  assets,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
  getAccounts,
  isAdmin,
}) {
  const [id, setId] = useState()
  const [version, setVersion] = useState()
  const [type, setType] = useState()
  const [name, setName] = useState()
  const [plaidName, setPlaidName] = useState()
  const [subType, setSubType] = useState()
  const [sortKey, setSortKey] = useState()
  const [amount, setAmount] = useState()
  const [subTypeClassification, setSubTypeClassification] = useState()
  const [interestRate, setInterestRate] = useState()
  const [monthlyPayment, setMonthlyPayment] = useState()
  const [frequency, setFrequency] = useState()
  const [loanEndDate, setLoanEndDate] = useState()
  const [modalTitle, setModalTitle] = useState()
  const [open, setOpen] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const handleViewAsset = ({
    id,
    typeSubTypeUuid,
    _version,
    name,
    plaidName,
    amount,
    subType,
    subTypeClassification,
  }) => {
    setType('asset')
    setId(id)
    setVersion(_version)
    setSortKey(typeSubTypeUuid)
    setName(name)
    setPlaidName(plaidName)
    setSubType(subType)
    setAmount(amount)
    setOpen(true)
    setCanEdit(true)
    setModalTitle(`Update ${name}`)
    setSubTypeClassification(subTypeClassification)
  }

  const handleLiabilityClickOpen = () => {
    setOpen(true)
    setSubType('')
    setType('liability')
    setModalTitle('Add a Liability')
  }

  const handleViewLiability = ({
    id,
    typeSubTypeUuid,
    _version,
    name,
    plaidName,
    amount,
    subType,
    subTypeClassification,
    interestRate,
    loanEndDate,
    monthlyPayment,
  }) => {
    setType('liability')
    setId(id)
    setVersion(_version)
    setSortKey(typeSubTypeUuid)
    setName(name)
    setPlaidName(plaidName)
    setSubType(subType)
    setAmount(amount)
    setOpen(true)
    setCanEdit(true)
    setModalTitle(`Update ${name}`)
    setSubTypeClassification(subTypeClassification)
    setInterestRate(interestRate)
    setLoanEndDate(loanEndDate)
    setMonthlyPayment(monthlyPayment)
  }

  const handleSubmit = () => {
    handleClose()
  }

  const handleClose = () => {
    setOpen(false)
    setCanEdit(false)
    setVersion('')
    setId('')
    setType('')
    setName('')
    setPlaidName('')
    setSubType('')
    setAmount('')
    setSortKey('')
    setModalTitle('')
    setSubTypeClassification('')
    setInterestRate('')
    setLoanEndDate('')
    setMonthlyPayment('')
    setFrequency('')
  }

  const handleResetForm = () => {
    // Don't touch open, type, modalTitle
    setCanEdit(false)
    setVersion('')
    setId('')
    setName('')
    setPlaidName('')
    setSubType('')
    setAmount('')
    setSortKey('')
    setSubTypeClassification('')
    setInterestRate('')
    setLoanEndDate('')
    setMonthlyPayment('')
    setFrequency('')
  }

  const handleAssetClickOpen = () => {
    setOpen(true)
    // setSubType('')
    setType('asset')
    setModalTitle('Add An Asset')
  }

  const hasThriftSavingsPlan = assets?.some(
    (item) =>
      item.externalAsset === true &&
      item.externalInstitutionName === 'Thrift Savings Plan'
  )

  const assetSum =
    (assets || []).map((assets) => assets.amount).reduce((a, b) => a + b, 0) +
    (hasThriftSavingsPlan
      ? 0
      : (intakeState.tspBalance ? intakeState.tspBalance : 0) +
        (intakeState.tspRothBalance ? intakeState.tspRothBalance : 0))

  const liabilitySum = (liabilities || [])
    .map((liabilities) => liabilities.amount)
    .reduce((a, b) => a + b, 0)

  const netWorth = assetSum - liabilitySum

  const [localForm, setLocalForm] = useState({
    financialAdvisorNotes: intakeState.financialAdvisorNotes || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  const handleReload = () => {
    getAccounts()
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth={'sm'}
        PaperProps={{
          sx: {
            overflow: { xs: 'auto', sm: 'visible' },
            margin: '16px',
            width: 'calc(100% - 32px)',
          },
        }}
      >
        <Box
          sx={{
            justifyContent: 'space-between',
            position: 'relative',
            display: 'flex',
            top: { xs: 8, sm: -48 },
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 0,
              right: { xs: 8, sm: 0 },
              color: '#fff',
              zIndex: 1,
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#000',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
        {/* add box to hide the overflow on transition */}
        <Box sx={{ overflow: { xs: 'auto', sm: 'hidden' } }}>
          <DialogContent
            sx={{ overflow: 'visible', paddingTop: { xs: '40px', sm: '16px' } }}
          >
            <motion.div
              key="accounts"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <Box sx={{ marginBottom: '16px' }}>
                <Typography variant={'headerTitle'} color={'#262627'}>
                  {modalTitle}
                </Typography>
              </Box>
              <NewBalanceSheetModalForm
                handleClose={handleClose}
                handleResetForm={handleResetForm}
                canEdit={canEdit}
                type={type}
                name={name}
                amount={amount}
                version={version}
                id={id}
                subType={subType}
                setVersion={setVersion}
                setId={setId}
                setType={setType}
                setSubType={setSubType}
                setSubTypeClassification={setSubTypeClassification}
                subTypeClassification={subTypeClassification}
                interestRate={interestRate}
                setInterestRate={setInterestRate}
                monthlyPayment={monthlyPayment}
                setMonthlyPayment={setMonthlyPayment}
                frequency={frequency}
                setFrequency={setFrequency}
                loanEndDate={loanEndDate}
                setLoanEndDate={setLoanEndDate}
                setName={setName}
                plaidName={plaidName}
                setAmount={setAmount}
                typeSubTypeUuid={sortKey}
                key={sortKey}
                open={true}
                handleSubmit={handleClose}
                reloadAccounts={handleReload}
                newStyle={true}
              />
            </motion.div>
          </DialogContent>
        </Box>
      </Dialog>
      <Grid item xs={12} md={4} xl={3}>
        <Card
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="valueTitle">VALUE POINTS</Typography>
              <Collapse
                in={collapseSections.balanceSheet}
                style={{ width: '100%' }}
              >
                {(liabilities || []).some(
                  (item) => !('interestRate' in item)
                ) && <ValuePoint text="Missing Interest Rate" type="warning" />}
                {(liabilities || []).some((item) => item.interestRate > 10) && (
                  <ValuePoint
                    text="Pay Off High Interest Debt"
                    type="warning"
                  />
                )}
                {(liabilities || [])
                  .filter((item) => item.subType === 'creditcard') // Filter items with subType 'checkingsavings'
                  .reduce((sum, item) => sum + (item.amount || 0), 0) >=
                  4000 && <ValuePoint text="Pay Off Credit Card Debt" />}
                {(liabilities || []).some(
                  (item) => item.subType === 'loan'
                ) && <ValuePoint text="Consider Upstart Loan Consolidation" />}

                <ValuePoint
                  text={`Savings Goal: ${dollarFormat.format(
                    intakeState?.savingsGoal || 10000
                  )}`}
                  type="donut"
                />
                {(assets || [])
                  .filter((item) => item.subType === 'checkingsavings') // Filter items with subType 'checkingsavings'
                  .reduce((sum, item) => sum + (item.amount || 0), 0) >=
                  (intakeState?.savingsGoal || 10000) && (
                  <ValuePoint text="Meeting Savings Goal" type="success" />
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="balancesheet"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse('balanceSheet')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Balance Sheet
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.balanceSheet
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse
            in={collapseSections.balanceSheet}
            style={{ width: '100%' }}
          >
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              {/* <Grid item xs={12} xxl={10} xl3={8}>
                <BalanceSheetRow
                  accounts={liabilities}
                  handleAddAccount={handleLiabilityClickOpen}
                  category={'AllLiabilities'}
                  handleViewAccount={handleViewLiability}
                  accountType="Liabilities"
                  addAccountButtonText="Add a Liability"
                />
                <BalanceSheetRow
                  accounts={assets}
                  handleAddAccount={handleAssetClickOpen}
                  category={'AllAssets'}
                  handleViewAccount={handleViewAsset}
                  accountType="Assets"
                  addAccountButtonText="Add An Asset"
                  assetsWithTSP={assetSum}
                />
                <Accordion
                  sx={{
                    '&.MuiPaper-root.MuiAccordion-root .MuiAccordionSummary-root':
                      {
                        padding: '0 12px 0 16px',
                      },
                    '&.MuiPaper-root.MuiAccordion-root.Mui-expanded': {
                      boxShadow: 'none',
                      marginTop: '0',
                      backgroundColor: '#fafafb',
                    },
                    '.MuiAccordionSummary-root:hover': {
                      backgroundColor: '#fafafb',
                    },
                    '.MuiAccordionSummary-root.Mui-expanded': {
                      minHeight: '48px',
                      backgroundColor: '#fafafb',
                      borderTop: '1px solid #ddd',
                    },
                    '.MuiAccordionSummary-content': {
                      margin: '0',
                      justifyContent: 'space-between',
                    },
                    '.MuiAccordionSummary-content>p': {
                      padding: '12px 0',
                      // minWidth: '135px',
                    },
                    '.MuiAccordionSummary-content>p:nth-of-type(2)': {
                      padding: '12px 0 12px 16px',
                      textAlign: 'right',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                  >
                    <Typography
                      textAlign="left"
                      color="#262627"
                      variant="body2"
                      fontWeight="bold"
                    >
                      Net Worth
                    </Typography>
                    <Typography
                      textAlign="right"
                      color="#262627"
                      variant="body2"
                      fontWeight="bold"
                      sx={{ marginRight: '10px' }}
                    >
                      {dollarFormat.format(netWorth)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography textAlign="center" color="#262627" pt={1}>
                      Total Assets - Total Liabilities<br></br>
                      {dollarFormat.format(assetSum)} -{' '}
                      {dollarFormat.format(liabilitySum)}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid> */}
              <Grid item xs={12} xxl={10} xl3={8} mb={2}>
                <NetWorthTable
                  getAccounts={getAccounts}
                  assets={assets}
                  liabilities={liabilities}
                  isAdmin={isAdmin}
                />
              </Grid>
              <Grid item xs={12} xxl={10} xl3={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} mt={1}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'hasFinancialAdvisor',
                      label: 'Has Financial Advisor',
                      placeholder: 'Has Financial Advisor',
                      inputType: 'select',
                      value: intakeState.hasFinancialAdvisor,
                      onChange: updateIntakeStateAndSubmit(
                        (hasFinancialAdvisor) =>
                          updateIntakeStateAtKey({ hasFinancialAdvisor })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  {intakeState?.hasFinancialAdvisor && (
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'financialAdvisorNotes',
                        label: 'Financial Advisor Notes',
                        placeholder: 'Financial Advisor Notes',
                        inputType: 'text',
                        value: localForm.financialAdvisorNotes,
                        onChange: (e) =>
                          handleChange('financialAdvisorNotes')(e),
                        onBlur: updateIntakeStateAndSubmit(
                          (financialAdvisorNotes) =>
                            updateIntakeStateAtKey({ financialAdvisorNotes })
                        ),
                        multiline: true,
                      })}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default BalanceSheet
